import React from 'react';
import { GatsbyBrowser, navigate } from 'gatsby';
import { HelmetProvider } from 'react-helmet-async';
import { prefetchGeoEdge } from './src/contexts/franchise/locator-provider';

type GatsbyPublicLoader = NonNullable<Window['___loader']>;

const isModifiedClick = (e: MouseEvent): boolean => e.metaKey || e.altKey || e.ctrlKey || e.shiftKey || e.button !== 0;

const isSameOrigin = (url: URL): boolean => url.origin === window.location.origin;

const shouldIgnoreAnchor = (a: HTMLAnchorElement, e: MouseEvent): boolean => {
    if (e.defaultPrevented || isModifiedClick(e)) return true;
    const target = a.target || document.querySelector('base')?.target;
    if (target && target.toLowerCase() !== '_self') return true;
    if (a.hasAttribute('download')) return true;
    const rel = a.getAttribute('rel')?.toLowerCase() ?? '';
    return rel.split(/\s+/).includes('external');
};

const removeRouterHash = (): void => {
    window.routerHash = undefined;
};

const findHashElement = (hash: string): HTMLElement | null => {
    const id = hash.replace(/^#/, '');
    let decoded = id;
    try {
        decoded = decodeURIComponent(id);
    } catch {
        // A lone `%` in a CMS heading ID can't be decoded; the literal lookup below covers it.
    }
    return document.getElementById(decoded) ?? document.getElementById(id);
};

const scrollTo = (hash: string): void => {
    const scrollOffset = -200; // Roughly the height of the sticky header.
    const el = findHashElement(hash);
    if (!el) return;

    const top = el.getBoundingClientRect().top + window.scrollY + scrollOffset;
    window.scrollTo({ top, behavior: 'smooth' });
    removeRouterHash();
};

// Bumped on every route change. A retry loop or pending delay started for an earlier
// route checks its own value, so it can neither scroll nor clear `routerHash` once the
// page has moved on - including when the next route carries the very same hash.
let scrollRun = 0;

const scrollToOnElementExists = (
    hash: string,
    run: number,
    timeoutMs = 1000, // Ignore the update after 1000ms if the ID can't be found
): void => {
    const start = performance.now();
    const tick = (): void => {
        if (run !== scrollRun || window.location.hash !== hash) return;
        if (findHashElement(hash)) {
            scrollTo(hash);
            return;
        }
        if (performance.now() - start < timeoutMs) {
            requestAnimationFrame(tick);
        } else {
            removeRouterHash();
        }
    };

    tick();
};

const scheduleScrollTo = (hash: string, delayMs = 0): void => {
    const run = scrollRun;
    if (!delayMs) {
        scrollToOnElementExists(hash, run);
        return;
    }
    setTimeout(() => {
        if (run === scrollRun) scrollToOnElementExists(hash, run);
    }, delayMs);
};

const isRealPage = (loader: GatsbyPublicLoader, resources: unknown, pagePath: string): boolean => {
    if (!resources || (resources as { status?: string }).status === 'error') return false;
    try {
        // `loadPage` resolves with the page resources payload, which carries no `notFound`
        // flag of its own - the loader keeps that on its internal page entry.
        return !loader.isPageNotFound(pagePath);
    } catch {
        return false;
    }
};

// Bumped per handled click and on every route update, so a slower resolution can't act
// after a newer click or a newer router transition - including a same-URL replacement.
let anchorRun = 0;

/*
 * Gatsby's `navigate` falls back to `window.location = pathname` when a path has no
 * built page, which drops the query string and the hash. Ask the loader first, so
 * endpoints, assets and server-only legacy redirects get a native navigation with the
 * complete URL while real pages still transition client-side.
 * */
const routeThroughGatsby = (loader: GatsbyPublicLoader, url: URL): void => {
    const pagePath = `${url.pathname}${url.search}`;
    const from = window.location.href;
    anchorRun += 1;
    const run = anchorRun;
    const settle = (clientSide: boolean): void => {
        // Another click, router transition, or outside location change already took over.
        if (run !== anchorRun || window.location.href !== from) return;
        // Native fallbacks assign the resolved absolute URL, so no pathname shape can send
        // a same-origin link off this origin.
        if (clientSide) navigate(`${url.pathname}${url.search}${url.hash}`);
        else window.location.assign(url.href);
    };

    // Nothing downstream of this `URL` handles a `//host`-style pathname: the loader
    // collapses the double slash to build its page-data URL, so an unrelated page answers
    // for it and the path looks real, and reach-router then assigns the raw protocol-relative
    // string once `pushState` rejects it. Only the server can resolve these.
    if (url.pathname.startsWith('//')) {
        settle(false);
        return;
    }

    let resolving: Promise<unknown>;
    try {
        resolving = Promise.resolve(loader.loadPage(pagePath));
    } catch {
        settle(false);
        return;
    }

    resolving
        .then(resources => settle(isRealPage(loader, resources, pagePath)))
        // A rejected page-data request means Gatsby has no usable page either.
        .catch(() => settle(false));
};

const buildAnchorHandler = () => (e: MouseEvent) => {
    const target = e.target as Element | null;
    const a = target?.closest?.('a');
    // SVG anchors expose an SVGAnimatedString `target`; leave those to the browser.
    if (!(a instanceof HTMLAnchorElement)) return;

    if (shouldIgnoreAnchor(a, e)) return;

    const hrefAttr = a.getAttribute('href')?.trim();
    if (!hrefAttr || hrefAttr === '#') return;

    let url: URL;
    try {
        url = new URL(hrefAttr, document.baseURI);
    } catch {
        return;
    }

    if (!/^https?:$/.test(url.protocol) || !isSameOrigin(url)) return;

    // React's Link and custom click handlers have already had a chance to run.
    // Route the remaining CMS HTML anchors through Gatsby instead of reloading.
    if (url.pathname === window.location.pathname && url.search === window.location.search) {
        // Already the current document: only the hash moves, so skip the loader round trip.
        e.preventDefault();
        navigate(`${url.pathname}${url.search}${url.hash}`);
        return;
    }

    // eslint-disable-next-line no-underscore-dangle -- Gatsby publishes its loader under this name.
    const loader = window.___loader;
    // Without the loader contract there is nothing to resolve against, and the native
    // click already keeps the whole URL.
    if (typeof loader?.loadPage !== 'function' || typeof loader.isPageNotFound !== 'function') return;

    e.preventDefault();
    routeThroughGatsby(loader, url);
};

export const onPreRouteUpdate: GatsbyBrowser['onPreRouteUpdate'] = ({ location, prevLocation }) => {
    scrollRun += 1;
    // A newer route supersedes any anchor resolution still in flight, even when it
    // lands on the very same URL, so a late loader result can no longer navigate.
    anchorRun += 1;
    // Track hashes for Gatsby Link transitions as well as CMS HTML anchors.
    // Initial deep links are handled once by onInitialClientRender.
    window.routerHash = prevLocation ? location.hash || undefined : undefined;
};

export const onRouteUpdate: GatsbyBrowser['onRouteUpdate'] = ({ location, prevLocation }) => {
    // find if we've got a hash defined - if we do - scroll to it
    const hash = window.routerHash;

    if (hash) {
        if (prevLocation?.pathname === location.pathname) {
            // Don't wait for transition if page is the same
            scheduleScrollTo(hash);
        } else {
            // Scroll when the element is rendered/available, after a 300ms initial delay
            // Doing without the 300ms delay entirely feels janky, keeping it in
            scheduleScrollTo(hash, 300);
        }
    }
};

export const onClientEntry: GatsbyBrowser['onClientEntry'] = () => {
    window.dataLayer = window.dataLayer || [];

    const handler = buildAnchorHandler();
    document.addEventListener('click', handler);
    return () => {
        document.removeEventListener('click', handler);
    };
};

export const onInitialClientRender: GatsbyBrowser['onInitialClientRender'] = () => {
    prefetchGeoEdge();
    const { hash } = window.location;
    if (hash) {
        scheduleScrollTo(hash, 300);
    }
};

/*
 * If the pathnames are the same, and there is a router hash, don't use in-built page scroll updating
 * */
export const shouldUpdateScroll: GatsbyBrowser['shouldUpdateScroll'] = ({
    prevRouterProps,
    routerProps,
}) => !(
    prevRouterProps?.location?.pathname === routerProps?.location?.pathname
    && routerProps?.location?.hash
);

export const wrapRootElement: GatsbyBrowser['wrapRootElement'] = ({
    element,
}): React.ReactElement => <HelmetProvider>{element}</HelmetProvider>;
